import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Image as ImageIcon,
  Images,
  Link2,
  Loader2,
  Play,
  Plus,
  Smile,
  X,
} from 'lucide-react';
import EmojiPicker, {
  EmojiStyle,
  Theme as EmojiTheme,
  type EmojiClickData,
} from 'emoji-picker-react';
import type { ProfileButton } from '@proptr/shared';
import { useTheme } from '@proptr/ui';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';
import { Field, Label } from './proptreeForm.js';
import { client } from '../main.js';

type ButtonKind = 'link' | 'video' | 'gallery';

const KIND_OPTIONS: Array<{
  kind: ButtonKind;
  label: string;
  description: string;
  Icon: typeof Link2;
}> = [
  {
    kind: 'link',
    label: 'Link button',
    description: 'Title, optional subtitle and an icon or image.',
    Icon: Link2,
  },
  {
    kind: 'video',
    label: 'Video',
    description: 'Embed a video. Title plus a video URL.',
    Icon: Play,
  },
  {
    kind: 'gallery',
    label: 'Photo gallery',
    description: 'Multiple images shown in a horizontal slideshow.',
    Icon: Images,
  },
];

export function AddButtonModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (button: ProfileButton) => void;
}) {
  const [kind, setKind] = React.useState<ButtonKind | null>(null);
  // Two-stage open/close so we can play an exit animation before unmounting.
  // `shown` controls the DOM mount; `visible` drives the transition classes.
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) setKind(null);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      // Two RAFs guarantee the initial (closed) classes paint before we
      // flip to the open state, so the browser actually animates the diff.
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!shown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--hairline))] px-5 py-4">
          <div>
            <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
              {kind ? KIND_OPTIONS.find((k) => k.kind === kind)!.label : 'Add button'}
            </h2>
            <p className="mt-1.5 font-mono text-[11px] text-muted-foreground">
              {kind
                ? 'Configure how this button looks on your proptree.'
                : 'Pick the type of button you want to add.'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X />
          </Button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
          {kind === null ? (
            <KindPicker onPick={setKind} />
          ) : (
            <KindForm
              kind={kind}
              onBack={() => setKind(null)}
              onAdd={(button) => {
                onAdd(button);
                onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function KindPicker({ onPick }: { onPick: (k: ButtonKind) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      {KIND_OPTIONS.map(({ kind, label, description, Icon }) => (
        <button
          key={kind}
          type="button"
          onClick={() => onPick(kind)}
          className="group flex items-start gap-4 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted dark:border-white/10 dark:bg-card-raised"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-sans text-[14px] font-semibold leading-tight">
              {label}
            </div>
            <p className="mt-1 font-mono text-[11px] leading-snug text-muted-foreground">
              {description}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

function newButtonId() {
  return Math.random().toString(36).slice(2, 12);
}

function KindForm({
  kind,
  onBack,
  onAdd,
}: {
  kind: ButtonKind;
  onBack: () => void;
  onAdd: (button: ProfileButton) => void;
}) {
  const [title, setTitle] = React.useState('');
  const [subtitle, setSubtitle] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [icon, setIcon] = React.useState<string | null>(null);
  const [image, setImage] = React.useState<string | null>(null);
  const [images, setImages] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const titleOk = title.trim().length > 0;
  const linkOk = kind !== 'link' || true;
  const videoOk = kind !== 'video' || url.trim().length > 0;
  const galleryOk = kind !== 'gallery' || images.length > 0;
  const canSubmit = titleOk && linkOk && videoOk && galleryOk;

  const submit = () => {
    if (!canSubmit) {
      setError(
        kind === 'video' && !videoOk
          ? 'A video URL is required.'
          : kind === 'gallery' && !galleryOk
            ? 'Add at least one image.'
            : 'Title is required.',
      );
      return;
    }
    onAdd({
      id: newButtonId(),
      title: title.trim(),
      subtitle: subtitle.trim() ? subtitle.trim() : null,
      url: url.trim(),
      icon: image ? null : icon,
      image,
      images: kind === 'gallery' ? images : null,
      enabled: true,
      type: kind,
    });
  };

  return (
    <div className="space-y-5">
      <Field
        label="Title"
        value={title}
        onChange={setTitle}
        placeholder={
          kind === 'video'
            ? 'Latest video'
            : kind === 'gallery'
              ? 'Photo gallery'
              : 'Button label'
        }
      />
      <Field
        label="Subtitle (optional)"
        value={subtitle}
        onChange={setSubtitle}
        placeholder="Short description"
      />

      {kind === 'link' && (
        <>
          <Field
            label="URL (optional)"
            value={url}
            onChange={setUrl}
            placeholder="https://…"
          />
          <IconOrImagePicker
            icon={icon}
            image={image}
            onIconChange={setIcon}
            onImageChange={setImage}
          />
        </>
      )}

      {kind === 'video' && (
        <>
          <Field
            label="Video URL"
            value={url}
            onChange={setUrl}
            placeholder="https://… (mp4 or YouTube)"
          />
          <IconOrImagePicker
            icon={icon}
            image={image}
            onIconChange={setIcon}
            onImageChange={setImage}
            imageLabel="Poster image"
          />
        </>
      )}

      {kind === 'gallery' && (
        <GalleryPicker images={images} onChange={setImages} />
      )}

      {error && (
        <p className="font-mono text-[11px] text-destructive">{error}</p>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button type="button" size="sm" onClick={submit} disabled={!canSubmit}>
          Add button
        </Button>
      </div>
    </div>
  );
}

// ---- Image upload primitives (re-uses the agent's Cloudinary signature) ----

async function uploadImage(file: File): Promise<string> {
  const sig = await client.agents.getProfileImageSignature();
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.apiKey);
  form.append('timestamp', String(sig.timestamp));
  form.append('folder', sig.folder);
  form.append('signature', sig.signature);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`,
    { method: 'POST', body: form },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Cloudinary returned ${res.status}: ${body.slice(0, 120)}`);
  }
  const json = (await res.json()) as { secure_url?: string };
  if (!json.secure_url) throw new Error('No URL returned from Cloudinary');
  return json.secure_url.replace(/\/upload\//, '/upload/c_scale,w_1200/');
}

// Combined picker: a tabbed row that switches between emoji selection and
// image upload. Setting one clears the other so render-time precedence
// stays simple (image > icon > default).
function IconOrImagePicker({
  icon,
  image,
  onIconChange,
  onImageChange,
  imageLabel = 'Image',
}: {
  icon: string | null;
  image: string | null;
  onIconChange: (icon: string | null) => void;
  onImageChange: (url: string | null) => void;
  imageLabel?: string;
}) {
  const [tab, setTab] = React.useState<'emoji' | 'image'>(
    image ? 'image' : 'emoji',
  );

  return (
    <div className="space-y-2">
      <Label>Icon or image (optional)</Label>
      <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
        <TabButton
          active={tab === 'emoji'}
          onClick={() => {
            setTab('emoji');
            onImageChange(null);
          }}
        >
          Emoji
        </TabButton>
        <TabButton
          active={tab === 'image'}
          onClick={() => {
            setTab('image');
            onIconChange(null);
          }}
        >
          {imageLabel}
        </TabButton>
      </div>
      {tab === 'emoji' ? (
        <EmojiPickerField value={icon} onChange={onIconChange} />
      ) : (
        <ImagePicker value={image} onChange={onImageChange} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// Emoji selection — preview chip on the left, "Pick emoji" button opens
// a full searchable picker (emoji-picker-react ships ~3700 emoji with
// built-in search). The popover is anchored under the trigger, closes on
// outside click or Escape.
function EmojiPickerField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (icon: string | null) => void;
}) {
  const { theme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{
    left: number;
    top: number;
    placeAbove: boolean;
  } | null>(null);

  // Anchor the popover to the trigger via fixed positioning so it can
  // escape the modal's overflow-y-auto container. Flip above the trigger
  // when there's not enough room below.
  React.useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const PICKER_H = 380;
      const margin = 8;
      const spaceBelow = window.innerHeight - r.bottom;
      const placeAbove = spaceBelow < PICKER_H + margin;
      setPos({
        left: r.left,
        top: placeAbove ? r.top - margin : r.bottom + margin,
        placeAbove,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-[22px] leading-none">
        {value ? value : <Smile className="h-4 w-4 text-muted-foreground" />}
      </div>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      >
        <Smile />
        {value ? 'Change emoji' : 'Pick emoji'}
      </Button>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
        >
          Remove
        </Button>
      )}
      {open && pos &&
        createPortal(
          <div
            ref={popoverRef}
            className="z-[60]"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              transform: pos.placeAbove ? 'translateY(-100%)' : undefined,
            }}
          >
            <EmojiPicker
              theme={theme === 'dark' ? EmojiTheme.DARK : EmojiTheme.LIGHT}
              emojiStyle={EmojiStyle.NATIVE}
              searchPlaceHolder="Search emoji…"
              width={340}
              height={380}
              previewConfig={{ showPreview: false }}
              onEmojiClick={(data: EmojiClickData) => {
                onChange(data.emoji);
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

function ImagePicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadImage(file));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
          {value ? (
            <img
              src={value}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {busy ? <Loader2 className="animate-spin" /> : <ImageIcon />}
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            disabled={busy}
          >
            Remove
          </Button>
        )}
      </div>
      {error && (
        <p className="font-mono text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}

function GalleryPicker({
  images,
  onChange,
}: {
  images: string[];
  onChange: (next: string[]) => void;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFiles = async (files: FileList) => {
    setBusy(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const f of Array.from(files)) {
        urls.push(await uploadImage(f));
      }
      onChange([...images, ...urls]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAt = (i: number) =>
    onChange(images.filter((_, j) => j !== i));

  return (
    <div className="space-y-2">
      <Label>Images</Label>
      <div className="grid grid-cols-3 gap-2">
        {images.map((src, i) => (
          <div
            key={i}
            className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted"
          >
            <img src={src} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute right-1 top-1 rounded-full bg-background/80 p-1 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
              aria-label={`Remove image ${i + 1}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={cn(
            'flex aspect-square items-center justify-center rounded-lg border border-dashed border-border bg-card text-muted-foreground transition-colors hover:bg-muted',
            busy && 'cursor-not-allowed opacity-60',
          )}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) void handleFiles(files);
        }}
      />
      {error && (
        <p className="font-mono text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}
